'use client'

import { useEffect, useRef, useState } from 'react';
import styles from './ua.module.scss';
import Tabs from '@/components/tab/Tabs';
import { buildCreditTiersDD, calcCashPayment, disclaimer, getDefaultAPR } from '@/utils/DR';
import { formatCurrency, formatNumber } from '@/utils/Formatters';
import KeyValue from '@/components/value/KeyValue';
import Field from '@/components/form/Field';
import StickyBar from '@/components/nav/StickyBar';
import Button from '@/components/button/Button';
import { DMDRProvider, DMVisibilityTracker, DM_EVENT_DR_DEAL_LOADED, DM_EVENT_TRADE_ESTIMATE, useDMEventListener, emitDMEvent, DMDealForm, DM_ERROR, DM_FIELDS } from '@dealermesh/react';
import { strings } from '@/utils/I18n';
import TradeSlideButton from '../trade/TradeSlideButton';
import useEventListener, { emitEvent } from '@/components/plugins/EventListener';
import LeadForm from '../lead/LeadForm';
import TradeForm from '../trade/TradeForm';
import TradeSlider from '@/components/filters/TradeSlider';
import Disclaimer from '@/components/panel/Disclaimer';
import ApiUtils from '@/utils/ApiUtils';
import { EVENT_CALC_ENGINE_LOADED, EVENT_SHOW_INLINE_LEAD_FORM, HASH_INLINE_LEAD_FORM, showInlineContactForm } from '@/utils/Helpers';
import ShowLeadFormButton from './ShowLeadFormButton';


export default function DMPaymentWidget(props) {
  
  const vehicle = props.vehicle
  const site = props.site
  
  const leadForm = useRef()
  const tradeForm = useRef()
  const tier = site.session.credit_tier
  const price = vehicle.prices?.price || vehicle.prices?.msrp
  const fApr = getDefaultAPR(vehicle, tier)
  const lRes = tier.lease_residual
  const lMiles = 10000
  const lMf = tier.lease_money_factor
  
  const initTradeInValue = site.budget?.tradeInValue || 0
  const initCashDown = site.budget?.cashDown || site.dr.default_cash_down
  const creditTiers = buildCreditTiersDD(site.dr.credit_tiers)
  const [isVisible, setIsVisible] = useState(false)
  const [dealType, setDealType] = useState('finance')
  
  const [quote,setQuote] = useState()
  const [financePayment, setFinancePayment] = useState(0)
  const [leasePayment, setLeasePayment] = useState(0)
  const [cashPayment, setCashPayment] = useState(calcCashPayment(price, 0, 0))
  const [monthlyPayment, setMonthlyPayment] = useState(0)
  const [tradeInValue, setTradeInValue] = useState(initTradeInValue)
  const [cashDown, setCashDown] = useState(initCashDown)
  const [financeTerm, setFinanceTerm] = useState()
  const [leaseTerm, setLeaseTerm] = useState()
  const [creditTier, setCreditTier] = useState(tier)
  const [leaseMile, setLeaseMile] = useState(lMiles)
  const [trade, setTrade] = useState(site.vehicles?.trade)
  const [aprRate, setAprRate] = useState(fApr)
  
  const [tradeFormExpended,setTradeFormExpanded] = useState(null)
  
  const [lead,setLead] = useState(null)
  const [leadFormVisible,setLeadFormVisible] = useState(false)
  const [leadPayload,setLeadPayload] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [lender, setLender] = useState('')
  
  const leaseTab = (vehicle.condition == 1 ? [{ key: 'lease', labels: [{ label: 'Lease', className: '' }, { label: formatCurrency(leasePayment), className: 'text-primary text-bold' }] }] : [])
  const financeTab = { key: 'finance', labels: [{ label: 'Finance', className: '' }, { label: formatCurrency(financePayment), className: 'text-primary text-bold' }] }
  const cashTab = { key: 'cash', labels: [{ label: 'Cash', className: '' }, { label: formatCurrency(cashPayment), className: 'text-primary text-bold' }] }
  
  const [tabItems,setTabItems] = useState([...leaseTab, financeTab, cashTab])
  
  const cashDownSlider = useRef()
  
  useDMEventListener(DM_EVENT_TRADE_ESTIMATE, event => {
    if (event.detail?.value) {
      setTradeInValue(event.detail?.value)
      setTrade(event.detail)
    }
  })

  useEventListener("trade_removed", event => {
    setTradeInValue(0)
  })

  useEventListener(EVENT_SHOW_INLINE_LEAD_FORM, event => {
    setLeadFormVisible(true)
    setTimeout(() => {
      leadForm?.current?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  })

  useEventListener(DM_ERROR, event => {
    const detail = event.detail
    if (detail?.path?.indexOf('v1/dr/quote') > -1 || detail?.path?.indexOf('v1/dr/quote_options') > -1) {
      setIsLoading(false)
    }
  })

  useDMEventListener(DM_EVENT_DR_DEAL_LOADED, event => {
    if (event.detail && (event.detail.finance || event.detail.lease)) {
      const quote = event.detail
      let lp, fp, cp;
      //Handle credit tier
      const tier = site.dr.credit_tiers.filter(e => parseInt(e.tier) == parseInt(quote?.credit_tier))[0]
      setCreditTier(tier)

      // Set lease payment
      if (dealType == 'lease' || quote.lease) {
        lp = quote.lease?.payment
        setLeasePayment(lp)
        setLeaseMile(quote.lease?.mileage)
        setLeaseTerm(parseInt(quote.lease?.term))
        setLender(quote?.lease.lender)
      }

      // Set finance term and payment
      if (dealType == 'finance' && quote.finance) {
        fp = quote.finance?.payment
        setFinancePayment(fp)
        setFinanceTerm(parseInt(quote.finance?.term))
        setAprRate(quote.finance?.rate)
        setLender(quote?.finance.lender)
      }

      // Switch deal type when finance deal type is selected and quote is not having finance
      if (dealType == 'finance' && !quote.finance) {
        onDealTypeChange(quote.lease ? 'lease' : 'cash')
      }
      // Switch deal type when lease deal type is selected and quote is not having lease
      if (dealType == 'lease' && !quote.lease) {
        onDealTypeChange(quote.finance ? 'finance' : 'cash')
      }

      // Update value on sldier is not considering 0 as value to set, for now ingoring 0 and 1
      if (quote?.down > -1 && quote?.down > 1) {
        setCashDown(quote?.down)
        setTimeout(() => {
          cashDownSlider.current.updateValue(quote?.down)
        }, 500)
      }

      // Set tradeIn value
      if (quote?.trade?.estimate) {
        setTradeInValue(quote?.trade?.estimate)
      }
      // Set cash payment
      cp = calcCashPayment(price, quote?.trade?.estimate || tradeInValue || 0, site.budget?.tradeInPayoff || 0)
      setCashPayment(cp)
      // Update payment values for UI
      const paymentVal = dealType == 'finance' ? fp : (dealType == 'lease') ? lp : cp 
      setMonthlyPayment(paymentVal)
      emitEvent("vdp_payment_update", { payment: paymentVal, dealType: dealType })

      // Set the deal type tabs.
      const leaseTab = (vehicle.condition == 1 ? [{ key: 'lease', labels: [{ label: 'Lease', className: '' }, { label: formatCurrency(lp || leasePayment), className: 'text-primary text-bold' }] }] : [])
      const financeTab = { key: 'finance', labels: [{ label: 'Finance', className: '' }, { label: formatCurrency(fp || financePayment), className: 'text-primary text-bold' }] }
      const cashTab = { key: 'cash', labels: [{ label: 'Cash', className: '' }, { label: formatCurrency(cp || cashPayment), className: 'text-primary text-bold' }] }
      setTabItems([...leaseTab, financeTab, cashTab])
      setIsLoading(false)
      // Update lead data
      updateLeadData()
      setQuote(quote)
      emitEvent(EVENT_CALC_ENGINE_LOADED)
    }
  })
  
  useEffect(() => {
    if (site.session?.vehicles?.trade?.value) {
      setTrade(site.session?.vehicles?.trade)
      setTradeInValue(site.session?.vehicles?.trade?.value)
    }
  }, [site.session?.vehicles?.trade?.value])

  useEffect(() => {
    emitEvent("vdp_payment_update", {payment: monthlyPayment, dealType: dealType})
  }, [])

  useEffect(() => {
    updateLeadData()
  }, [monthlyPayment,financePayment,leasePayment,cashPayment])

  /**
   * 
   * @param {*} val 
   */
  const onVisible = (val) => {
    // console.log("VISIBLE - " + val)
    setIsVisible(val)
  }
  
  /**
   * 
   * @param {*} tab 
   */
  const onDealTypeChange = (tab) => {
    console.log('ON DEAL TYPE CHANGE')
    setDealType(tab.key)
    let paymentVal = 0
    if (tab.key == 'finance') {
      paymentVal = financePayment
    } else if (tab.key == 'lease') {
      paymentVal = leasePayment
    } else if (tab.key == 'cash') {
      paymentVal = cashPayment
    }
    setMonthlyPayment(paymentVal)
    emitEvent("vdp_payment_update", {payment: paymentVal, dealType: tab.key})
  }

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const setFieldValue = (field, value) => {
    if (field != 'cashDown' && field != 'tradeInValue') setIsLoading(true)
    if (field == 'creditTier') {
      const tier = site.dr.credit_tiers.filter(e => parseInt(e.tier) == parseInt(value))[0]
      setCreditTier(tier)
    } else if (field == 'cashDown') {
      setCashDown(parseInt(value))
    } else if (field == 'tradeInValue') {
      setTradeInValue(parseInt(value))
    }
  }

  const nextSteps = (e) => {
    showInlineContactForm()
  }
  
  const onLeadSubmit = (data) => {
    setLead(data)
  }

  const updateLeadData = () => {
    setTimeout(() => {
      let cond = "new"
      if (vehicle.condition == 2) cond = "pre-owned"
      if (vehicle.condition == 3) cond = "certified"
      let dealData = null
      if (dealType == "finance") {
        dealData = {
          "payment": monthlyPayment,
          "term": financeTerm,
          "rate": fApr,
          "fees": [],
          "offers": [],
          "plans": [],
        }
      } else if (dealType == "lease") {
        dealData = {
          "payment": monthlyPayment,
          "term": leaseTerm,
          "mileage": lMiles,
          "money_factor": lMf,
          "residual": lRes,
          "fees": [],
          "offers": [],
          "plans": [],
        }        
      }
      setLeadPayload({
        vid: ApiUtils.visitorId(),
        vin: vehicle.vin,
        dealer_id: vehicle.dealer.dealer_id,
        deal_type: dealType,
        quote: {
          "credit_tier": creditTier.tier,
          "credit_score": null,
          "down": cashDown,
          "deal_type": dealType,
          "vin": vehicle.vin,
          "vehicle": {
            "vin": vehicle.vin,
            "msrp": vehicle.msrp,
            "sale_price": vehicle.price
          },
          [dealType]: dealData,
          "trade": {
            "estimate": tradeInValue,
            "payoff": 0
          },
          "attachments": [
          ],
          "deposit": null,
          "delivery": null,
          "application": {}
        },
        vehicle: {
          "vehicle_type": null,
          "vin": vehicle.vin,
          "stock_number": vehicle.stock_number,
          "condition": cond,
          "name": vehicle.full_name,
          "year": vehicle.year,
          "make_name": vehicle.make_name,
          "model_name": vehicle.model_name,
          "trim_name": vehicle.trim_name,
          "msrp": vehicle.prices.msrp,
          "sale_price": vehicle.prices.price || vehicle.prices.msrp
        },
        lead_provider: {
          "provider_name":"Unison",
          "service_name":"Unison Websites",
          "lead_type":"New",
          "provider_value":"Sales"
        }
      })
    }, 500)
  }

  const getVehicleInfo = () => {
    const condition = vehicle.condition == 1 ? 'New' : 'Used'
    return {
      vin: vehicle?.vin,
      msrp: vehicle?.prices?.msrp,
      price: vehicle?.prices?.price,
      sticker_price: vehicle?.prices?.sticker_price,
      year: vehicle?.year,
      make: vehicle?.make_name,
      condition: condition,
      zip_code: site.session?.vehicles?.location?.zip,
      mileage: vehicle?.mileage
    }
  }

  return <>
    <DMVisibilityTracker onChange={ v => onVisible(v) } minTopValue={12}>
      <DMDRProvider car={getVehicleInfo()} down={initCashDown} creditTier={tier?.tier || 1} creditScore={(parseInt(tier?.max || 900) + parseInt(tier?.min || 900)) / 2} tradeInValue={initTradeInValue}>
        { quote &&
        <section className='white'>
          {isLoading && <div className={styles.mask}><div className={styles.spinner}><div></div><div></div><div></div><div></div></div></div>}
          <a name="payments"></a>
          <section>
            <h2 className={ styles.h + ' ' + styles.h2 }>Ways To Pay</h2>
            <Tabs items={tabItems} selected={'finance'} onChange={tab => onDealTypeChange(tab)}></Tabs>
          </section>
          <section>
            <DMDealForm className={ styles.form + ' ' + (props.theme ? styles[props.theme] : '') + ' ' + (props.className || '') }>
            <div id="vdp_payment_widget" name="VDP Payment Widget" className={ styles.pw }>
              { dealType != 'cash' &&
              <div>
                  <KeyValue label={strings(props.site)['dr_cashDown']} value={formatCurrency(cashDown)}></KeyValue>
                  <div className={ styles.pwsldr }>
                    <Field ref={cashDownSlider} type="slider" min="0" max={ 0.75 * price } value={cashDown || 1} step={100} onChange={value => setFieldValue('cashDown', value)} name={DM_FIELDS.DR_CASH_DOWN} debounce={500} blur={false}></Field>
                  </div>
              </div>
              }
              <div>
                <TradeSlider {...props} vehicle={vehicle} tradeInValue={tradeInValue} trade={trade} sliderClass={ styles.pwsldr } toggleForm={e => setTradeFormExpanded(!tradeFormExpended)} onChange={value => setFieldValue('tradeInValue', value)}></TradeSlider>
              </div>
              { tradeFormExpended &&
              <div>
                <TradeForm {...props} close={e => setTradeFormExpanded(!tradeFormExpended)}></TradeForm>
              </div>
              }
              { dealType == 'finance' &&
              <div>
                  <Field inline={true} labelWidth={ '100px' } name={DM_FIELDS.DR_FINANCE_TERM} label={strings(props.site)['dr_loanTerms']} suffix="months" type='select' onChange={value => setFieldValue('financeTerm', value)}></Field>
              </div>
              }
              { dealType == 'lease' &&
                <>
                  <div>
                      <Field inline={true} labelWidth={ '100px' } name={DM_FIELDS.DR_LEASE_TERM} label={strings(props.site)['dr_leaseTerms']} suffix="months" type='select' onChange={value => setFieldValue('leaseTerm', value)}></Field>
                  </div>
                  <div>
                      <Field inline={true} labelWidth={ '100px' } name={DM_FIELDS.DR_LEASE_MILES} label={strings(props.site)['dr_leaseMiles'] || 'Lease Miles'} suffix="miles" type="select" onChange={value => setFieldValue('leaseMile', value)}></Field>
                  </div>
                </>
              }
              { dealType != 'cash' && !isLoading &&
              <div>
                  <Field inline={true} labelWidth={ '100px' } name={DM_FIELDS.DR_CREDIT_TIER} label={strings(props.site)['dr_creditRange']} type="select" options={creditTiers} value={creditTier.tier} onChange={value => setFieldValue('creditTier', value)}></Field>
              </div>
              }
            </div>
            </DMDealForm>
          </section>
          <section>
            <h3 className={ styles.h + ' ' + styles.h3 }>{ strings(props.site)['dr_estPricingSummary'] }</h3>
          </section>
          <section>
            <div className={ styles.pws }>
              <div className={ styles.pwsl }>Vehicle Price</div>
              <div className={ styles.pwsv }>{ formatCurrency(price) }</div>
            </div>
          </section>
          <section className={ styles.divider }></section>
          <section>
            <div className={ styles.pws }>
              <div className={ styles.pwsl }>Trade-in Added</div>
              <div className={ styles.pwsv }>{ formatCurrency(tradeInValue) }</div>
            </div>
          </section>
          { dealType != 'cash' &&
          <section>
            <div className={ styles.pws }>
              <div className={ styles.pwsl }>Cash Down</div>
              <div className={ styles.pwsv }>{ formatCurrency(cashDown) }</div>
            </div>
          </section>
          }
          { dealType != 'cash' &&
          <section className={ styles.divider }></section>
          }
          { dealType == 'finance' &&
          <section>
            <div className={ styles.pws }>
              <div className={ styles.pwsl }>Loan Term</div>
              <div className={ styles.pwsv }>{ formatNumber(financeTerm) } months</div>
            </div>
          </section>
          }
          { dealType == 'finance' &&
            <section>
              <div className={ styles.pws }>
                <div className={ styles.pwsl }>APR</div>
                <div className={ styles.pwsv }>{ formatNumber(aprRate || getDefaultAPR(vehicle, creditTier),2) }%</div>
              </div>
            </section>
          }
          { dealType == 'finance' && 
            <section>
              <div className={ styles.pws }>
                <div className={ styles.pwsl }>Lender</div>
                <div className={ styles.pwsv }>{lender}</div>
              </div>
            </section>
          }
          { dealType == 'lease' &&
             <section>
              <div className={ styles.pws }>
                <div className={ styles.pwsl }>Lease Term</div>
                <div className={ styles.pwsv }>{ formatNumber(leaseTerm) } months</div>
              </div>
            </section>
          }
          { dealType == 'lease' &&
            <section>
              <div className={ styles.pws }>
                <div className={ styles.pwsl }>Annual Mileage</div>
                <div className={ styles.pwsv }>{ formatNumber(leaseMile) } miles</div>
              </div>
            </section>
          }
          { dealType == 'lease' &&
             <section>
              <div className={ styles.pws }>
                <div className={ styles.pwsl }>Lender</div>
                <div className={ styles.pwsv }>{lender}</div>
              </div>
            </section>
          }
          <section className={ styles.divider }></section>
          <section>
            <div className={ styles.pws }>
              <div className={ styles.pwsl }>Taxes & Fees *</div>
              <div className={ styles.pwsv }>{ 'Not included*' }</div>
            </div>
          </section>
          <section ref={leadForm} className='primary'>
            <section id={ HASH_INLINE_LEAD_FORM }>
              <div className='flex-column flex-align-middle flex-with-gap'>
                <div className='flex-col text-sm'>
                  { dealType == 'cash' ? 'Payment' : 'Monthly Payment' }
                </div>
                { dealType == 'finance' &&
                <div className='flex-col text-xl text-bold text-secondary'>
                  { formatCurrency(monthlyPayment) }<span className='text-white text-normal'>*</span>
                </div>
                }
                { dealType == 'lease' &&
                <div className='flex-col text-xl text-bold text-secondary'>
                  { formatCurrency(monthlyPayment) }<span className='text-white text-normal'>*</span>
                </div>
                }
                { dealType == 'cash' &&
                <div className='flex-col text-xl text-bold text-secondary'>
                  { formatCurrency(monthlyPayment) }<span className='text-white text-normal'>*</span>
                </div>
                }
                { !lead && strings(props.site)['vdp_leadFormMessage'] &&
                <div className='flex-col text-align-center'>
                  <span>{ strings(props.site)['vdp_leadFormMessage'] }</span>
                </div>
                }
                { !lead && strings(props.site)['vdp_leadFormMessage'] &&
                <div className='flex-col'>
                </div>
                }
                { !leadFormVisible &&
                <div className='flex-col'>
                  <Button theme={["secondary","inline"]} style={{ width: '70vw', maxWidth: '240px'}} label={ strings(props.site)['vdp_leadFormNextSteps'] } onClick={e => nextSteps(e)}>
                    <div className='text-bold'>{ strings(props.site)['vdp_leadFormNextSteps'] }</div>
                  </Button>
                </div>
                }
                { !lead && leadFormVisible &&
                <div className='flex-col'>
                  <LeadForm {...props} refData={leadPayload} onSubmit={data => onLeadSubmit(data)}></LeadForm>
                </div>
                }
                { lead &&
                <div className='flex-col text-align-center'>
                </div>
                }
                { lead &&
                <div className='flex-col text-align-center text-bold text-md'>
                  <span>{ strings(props.site)['vdp_leadReceivedMessage'] }</span>
                </div>
                }
                { lead &&
                <div className='flex-col text-align-center text-bold text-md'>
                  <span>If you need to contact us, please call us at<br></br><a className="text-secondary" href={ "tel:" + props.dealer?.sales_phone }>{ props.dealer?.sales_phone }</a>.</span>
                </div>
                }
                { lead &&
                <div className='flex-col text-align-center'>
                </div>
                }
              </div>
            </section>
            <section></section>
          </section>
          <section></section>
          { dealType && props.site.dr[dealType] && props.site.dr[dealType].disclaimer &&
          <section>
            <Disclaimer disclaimer={ "*" + disclaimer(site, dealType, {
              monthly: monthlyPayment,
              apr: getDefaultAPR(vehicle, creditTier),
              term: dealType == 'finance' ? financeTerm : leaseTerm,
              down: cashDown,
              mf: creditTier.lease_money_factor,
              residual: creditTier.lease_residual
            }) }></Disclaimer>
          </section>
          }
          { props.site.dr.disclaimer &&
          <section>
            <Disclaimer disclaimer={ disclaimer(site, null, {
              monthly: monthlyPayment,
              apr: getDefaultAPR(vehicle, creditTier),
              term: dealType == 'finance' ? financeTerm : leaseTerm,
              down: cashDown,
              mf: creditTier.lease_money_factor,
              residual: creditTier.lease_residual
            })}></Disclaimer>
          </section>
          }
        </section>
        }
      </DMDRProvider>
    </DMVisibilityTracker>
    { !isVisible &&
      <StickyBar {...props} location={'bottom'} mobile={true}>
        <section className="row">
          <ShowLeadFormButton {...props} labelKey="vdp_leadFormTopGetStarted"></ShowLeadFormButton>
        </section>
      </StickyBar>
    }
    <TradeSlideButton {...props} ref={tradeForm}></TradeSlideButton>
  </>
  
}
